import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend, LabelList
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import type { DailyPoint, RankedItem, TrendData } from '../../utils/dashboardStats';

const TOOLTIP_STYLE = { background: '#1F2937', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#F9FAFB' };
const AXIS_TICK = { fontSize: 11, fill: '#6B7280' };
// Paleta de respaldo para cubetas sin color propio (p. ej. categorías de alerta).
const FALLBACK_COLORS = ['#3B82F6', '#F59E0B', '#10B981', '#8B5CF6', '#EF4444', '#0EA5E9', '#14B8A6', '#64748B', '#F97316'];
const colorOf = (item: RankedItem, index: number) => item.color ?? FALLBACK_COLORS[index % FALLBACK_COLORS.length];

export function TrendChart({ data }: { data: TrendData[] }) {
  const formatted = data.map(d => ({
    ...d,
    label: format(parseISO(d.date), 'dd MMM', { locale: es }),
  }));

  return (
    <ResponsiveContainer width="100%" height={220}>
      <AreaChart data={formatted} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
        <defs>
          <linearGradient id="gradCritical" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="gradMedium" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="gradLow" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
        <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={false} interval={4} />
        <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={false} />
        <Tooltip
          contentStyle={{ background: '#1F2937', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#F9FAFB' }}
          labelStyle={{ color: '#9CA3AF', marginBottom: 4 }}
        />
        <Area type="monotone" dataKey="critical" name="Críticas" stroke="#EF4444" fill="url(#gradCritical)" strokeWidth={2} />
        <Area type="monotone" dataKey="medium" name="Medias" stroke="#F59E0B" fill="url(#gradMedium)" strokeWidth={2} />
        <Area type="monotone" dataKey="low" name="Bajas" stroke="#10B981" fill="url(#gradLow)" strokeWidth={2} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

interface SeverityDonutProps {
  critical: number;
  medium: number;
  low: number;
}

const DONUT_COLORS = ['#EF4444', '#F59E0B', '#10B981'];

export function SeverityDonut({ critical, medium, low }: SeverityDonutProps) {
  const data = [
    { name: 'Críticas', value: critical },
    { name: 'Medias', value: medium },
    { name: 'Bajas', value: low },
  ];
  const total = critical + medium + low;

  return (
    <div style={{ position: 'relative', width: '100%', height: 200 }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data} cx="50%" cy="50%" innerRadius={55} outerRadius={80}
            paddingAngle={3} dataKey="value"
          >
            {data.map((_, i) => (
              <Cell key={i} fill={DONUT_COLORS[i]} />
            ))}
          </Pie>
          <Legend
            iconType="circle"
            iconSize={8}
            formatter={(value) => <span style={{ color: '#9CA3AF', fontSize: 12 }}>{value}</span>}
          />
          <Tooltip
            contentStyle={{ background: '#1F2937', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#F9FAFB' }}
          />
        </PieChart>
      </ResponsiveContainer>
      {/* Center label — variables de tema, no un color fijo pensado solo para
          modo oscuro: '#F9FAFB' quedaba blanco sobre el fondo claro del card
          en modo claro (issue reportado en vivo). */}
      <div style={{ position: 'absolute', top: '42%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center', pointerEvents: 'none' }}>
        <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-primary)' }}>{total}</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>eventos</div>
      </div>
    </div>
  );
}

type ValueFormatter = (value: number) => string;
const asCount: ValueFormatter = v => String(v);

interface RankedBarChartProps {
  data: RankedItem[];
  valueFormatter?: ValueFormatter;
  /** Nombre de la serie en el tooltip: "Alertas", "Gasto"… */
  seriesName: string;
}

/** Barras horizontales ordenadas: para comparar "quién tiene más", no para ver una evolución. */
export function RankedBarChart({ data, valueFormatter = asCount, seriesName }: RankedBarChartProps) {
  return (
    <ResponsiveContainer width="100%" height={Math.max(140, data.length * 36 + 16)}>
      <BarChart data={data} layout="vertical" margin={{ top: 0, right: 84, left: 0, bottom: 0 }}>
        <XAxis type="number" hide />
        <YAxis
          type="category" dataKey="label" width={120} tickLine={false} axisLine={false}
          tick={{ ...AXIS_TICK, fill: 'var(--text-secondary)' }}
          tickFormatter={(label: string) => (label.length > 18 ? `${label.slice(0, 17)}…` : label)}
        />
        <Tooltip
          cursor={{ fill: 'rgba(255,255,255,0.04)' }}
          contentStyle={TOOLTIP_STYLE}
          formatter={(value) => [valueFormatter(Number(value)), seriesName]}
        />
        <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={18}>
          {data.map((item, i) => <Cell key={item.key} fill={colorOf(item, i)} />)}
          {/* Etiqueta directa al final de cada barra: el valor se lee sin pasar el ratón. */}
          <LabelList dataKey="value" position="right" formatter={(v: unknown) => valueFormatter(Number(v))} style={{ fontSize: 11, fill: 'var(--text-secondary)' }} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

interface CategoryDonutProps {
  data: RankedItem[];
  centerLabel: string;
  valueFormatter?: ValueFormatter;
  /** Lo que va en el centro. Por defecto, la suma; para dinero, pásalo ya formateado. */
  centerValue?: string;
}

export function CategoryDonut({ data, centerLabel, valueFormatter = asCount, centerValue }: CategoryDonutProps) {
  const total = data.reduce((sum, d) => sum + d.value, 0);

  return (
    <div style={{ position: 'relative', width: '100%', height: 220 }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={data} cx="50%" cy="46%" innerRadius={55} outerRadius={80} paddingAngle={3} dataKey="value" nameKey="label">
            {data.map((item, i) => <Cell key={item.key} fill={colorOf(item, i)} />)}
          </Pie>
          <Legend
            iconType="circle" iconSize={8}
            formatter={(value) => <span style={{ color: '#9CA3AF', fontSize: 12 }}>{value}</span>}
          />
          <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(value) => valueFormatter(Number(value))} />
        </PieChart>
      </ResponsiveContainer>
      <div style={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center', pointerEvents: 'none' }}>
        <div style={{ fontSize: centerValue && centerValue.length > 8 ? 16 : 24, fontWeight: 700, color: 'var(--text-primary)' }}>
          {centerValue ?? total}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{centerLabel}</div>
      </div>
    </div>
  );
}

interface DailyTrendChartProps {
  data: DailyPoint[];
  color: string;
  seriesName: string;
  valueFormatter?: ValueFormatter;
}

/** Una sola serie por día (novedades, gasto total). La de tres severidades es `TrendChart`. */
export function DailyTrendChart({ data, color, seriesName, valueFormatter = asCount }: DailyTrendChartProps) {
  const formatted = data.map(d => ({ ...d, label: format(parseISO(d.date), 'dd MMM', { locale: es }) }));
  const gradientId = `gradDaily-${color.replace('#', '')}`;

  return (
    <ResponsiveContainer width="100%" height={220}>
      <AreaChart data={formatted} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.3} />
            <stop offset="95%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
        <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={false} minTickGap={24} />
        <YAxis
          tick={AXIS_TICK} tickLine={false} axisLine={false} width={48} allowDecimals={false}
          tickFormatter={(v: number) => (Math.abs(v) >= 1_000_000 ? `${v / 1_000_000}M` : Math.abs(v) >= 1000 ? `${v / 1000}k` : String(v))}
        />
        <Tooltip
          contentStyle={TOOLTIP_STYLE}
          labelStyle={{ color: '#9CA3AF', marginBottom: 4 }}
          formatter={(value) => [valueFormatter(Number(value)), seriesName]}
        />
        <Area type="monotone" dataKey="value" name={seriesName} stroke={color} fill={`url(#${gradientId})`} strokeWidth={2} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

interface ScoreGaugeProps {
  score: number;
  size?: number;
}

export function ScoreGauge({ score, size = 120 }: ScoreGaugeProps) {
  const color = score >= 75 ? '#10B981' : score >= 45 ? '#F59E0B' : '#EF4444';
  const r = (size - 16) / 2;
  const circ = 2 * Math.PI * r;
  const progress = (score / 100) * circ * 0.75;
  const cx = size / 2;

  return (
    <svg width={size} height={size * 0.85} viewBox={`0 0 ${size} ${size * 0.85}`}>
      {/* Track */}
      <circle cx={cx} cy={cx} r={r} fill="none" stroke="var(--border-strong)"
        strokeWidth={12} strokeDasharray={`${circ * 0.75} ${circ}`} strokeDashoffset={0}
        strokeLinecap="round" transform={`rotate(135 ${cx} ${cx})`} opacity={0.5} />
      {/* Progress */}
      <circle cx={cx} cy={cx} r={r} fill="none" stroke={color}
        strokeWidth={12} strokeDasharray={`${progress} ${circ}`}
        strokeLinecap="round" transform={`rotate(135 ${cx} ${cx})`}
        style={{ filter: `drop-shadow(0 0 6px ${color}88)` }} />
      {/* Score text */}
      <text x={cx} y={cx + 6} textAnchor="middle" fill="var(--text-primary)" fontSize={size * 0.22} fontWeight={700}>
        {Math.round(score)}
      </text>
      <text x={cx} y={cx + size * 0.2} textAnchor="middle" fill="var(--text-muted)" fontSize={size * 0.1}>
        /100
      </text>
    </svg>
  );
}
